import type { ReactNode } from 'react';

import { TableEmptyState } from '@/features/table/components/TableEmptyState';
import { InboxOutlined } from '@ant-design/icons';
import type { CustomNoRowsOverlayProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

export interface NoRowsOverlayContext {
  icon?: ReactNode;
  title?: string;
  description?: string;
  actionLabel?: string;
  actionIcon?: ReactNode;
  onAction?: () => void;
  filteredTitle?: string;
  filteredDescription?: string;
  /** Internal: set by useNoRowsOverlay hook */
  _isFiltered?: boolean;
}

export const NoRowsOverlay = (props: CustomNoRowsOverlayProps<unknown, NoRowsOverlayContext | undefined>) => {
  const { t } = useTranslation();
  const context: Partial<NoRowsOverlayContext> = props.context ?? {};

  const isFiltered = context._isFiltered ?? false;

  const source = isFiltered
    ? {
        title: context.filteredTitle,
        description: context.filteredDescription,
        fallbackTitle: t('tableEmptyState.noResults'),
        fallbackDescription: t('tableEmptyState.noResultsDescription')
      }
    : {
        title: context.title,
        description: context.description,
        fallbackTitle: t('tableEmptyState.noData'),
        fallbackDescription: undefined
      };

  const title = source.title ?? source.fallbackTitle;
  const description = source.description ?? source.fallbackDescription;

  return (
    <TableEmptyState
      icon={context.icon ?? <InboxOutlined />}
      title={title}
      description={description}
      actionLabel={context.actionLabel}
      actionIcon={context.actionIcon}
      onAction={context.onAction}
    />
  );
};
